import { ReactNode, useState, useEffect } from 'react';

import { useClickAway } from '@uidotdev/usehooks';
import { Check, PenSquare } from 'lucide-react';

import { DiarizationCorrectionIndicator } from './DiarizationCorrectionIndicator';
import { DiarizationCorrectionPopover } from './DiarizationCorrectionPopover';
import { LiveTranscriptBlock, isLiveBlock } from '../types/transcriptTypes';
import { formatTimestamp } from '../utils/timeUtils';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { TranscriptBlock, TranscriptSpeaker } from '@/api/types/backend-dto-types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/ShadCnSelect';
import AudioPlayer from '@/components/molecules/AudioPlayer';

interface BaseTranscriptBlockProps {
  block: TranscriptBlock | LiveTranscriptBlock;
  caseId?: string;
  transcriptId?: string;
  isActive?: boolean;
  onClick?: () => void;
  onAcceptCorrection?: (correctionId: string, blockId: string) => void;
  onRejectCorrection?: (correctionId: string, blockId: string) => void;
  isProcessing?: boolean;
  openPopoverId?: string | null;
  onPopoverOpenChange?: (correctionId: string, open: boolean) => void;
  className?: string;
  children?: ReactNode;
  // Editing props
  speakers?: TranscriptSpeaker[];
  onUpdate?: (blockId: string, content?: string, speakerId?: string | null) => void;
}

export const BaseTranscriptBlock = ({
  block,
  caseId,
  transcriptId,
  isActive = false,
  onClick,
  onAcceptCorrection,
  onRejectCorrection,
  isProcessing = false,
  openPopoverId,
  onPopoverOpenChange,
  className = '',
  children,
  speakers = [],
  onUpdate,
}: BaseTranscriptBlockProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(block.content);
  const [editedSpeakerId, setEditedSpeakerId] = useState<string | null>(block.internalSpeaker?.speaker?.id ?? null);
  // const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = useClickAway((event) => {
    if (isEditing) {
      // Don't cancel if clicking on a select dropdown or its portal content
      const target = event.target as Element;
      if (
        target.closest('[role="listbox"]') ||
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-radix-select-content]') ||
        target.closest('button')
      ) {
        return;
      }

      handleCancelEdit();
    }
  }) as React.RefObject<HTMLTextAreaElement>;

  const internalSpeaker = block.internalSpeaker;
  const internalName = internalSpeaker?.internalName;

  const logicalSpeaker = internalSpeaker?.speaker ?? speakers?.find((s) => s.id === internalSpeaker?.speakerId);

  const displayName = logicalSpeaker?.name ?? internalName;
  const color = logicalSpeaker?.color ?? 'gray';

  // Check if this is a live block
  const isLive = isLiveBlock(block);
  const isPartial = isLive && block.isPartial;
  const audioReady = !isLive || block.audioReady !== false;

  // Reset edited values when block changes
  useEffect(() => {
    setEditedContent(block.content);
    setEditedSpeakerId(block.internalSpeaker?.speaker?.id ?? null);
  }, [block]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      const length = editedContent.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  // Build class names for the block container
  const containerClasses = [
    'relative py-3 pl-2 border-l-2',
    isActive ? 'border-blue-400 bg-blue-50/30 transform -translate-y-1 shadow-lg rounded-r-md' : 'border-transparent',
    !isActive && !isPartial && 'hover:border-gray-200',
    onClick && 'cursor-pointer',
    'transition-all duration-200',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handlePopoverChange = (open: boolean) => {
    if (block.correction && onPopoverOpenChange) {
      if (open) {
        trackCustomEvent(TrackingEvents.ClickDiarizationCorrection);
      }
      onPopoverOpenChange(block.correction.id, open);
    }
  };

  const handleCancelEdit = () => {
    setEditedContent(block.content);
    setEditedSpeakerId(block.internalSpeaker?.speaker?.id ?? null);
    setIsEditing(false);
  };

  const handleContentEdit = (newContent: string) => {
    if (!onUpdate) return;

    const hasContentChanged = newContent !== block.content;

    if (!hasContentChanged) {
      setIsEditing(false);
      return;
    }

    trackCustomEvent(TrackingEvents.EditTranscriptBlockText);
    onUpdate(block.id, newContent, undefined);
    setIsEditing(false);
  };

  const handleSpeakerEdit = (newSpeakerId: string) => {
    if (!onUpdate) return;
    trackCustomEvent(TrackingEvents.EditTranscriptBlockSpeaker);
    onUpdate(block.id, undefined, newSpeakerId);
  };

  return (
    <div id={`block-${block.id}`} data-segment-id={block.id} className={containerClasses} onClick={onClick}>
      {/* Top row: Speaker header, edit button, and timestamp */}
      <div className="mb-2 flex items-start justify-between">
        <div className="flex items-center gap-3">
          {/* Timestamp on the left */}
          <div className="min-w-[50px] text-xs text-gray-400">{formatTimestamp(block.timestamp)}</div>

          {/* Speaker name and audio player */}
          <div className="flex items-center gap-2">
            {/* Colored dot indicator for speaker */}
            <div className={`h-2 w-2 rounded-full bg-${isPartial ? 'yellow' : color}-400`} />

            {!isPartial && transcriptId && caseId ? (
              <div data-audio-player>
                <AudioPlayer
                  blockId={block.id}
                  caseId={caseId}
                  transcriptId={transcriptId}
                  customColors={{
                    bg: 'bg-gray-100',
                    bgHover: 'hover:bg-gray-200',
                    text: 'text-gray-600',
                    border: 'border border-gray-300',
                  }}
                  className="h-5 w-5"
                  isProcessing={!audioReady}
                />
              </div>
            ) : null}

            {isPartial ? (
              <p className={`text-xs font-semibold text-yellow-600`}>Active Speaker</p>
            ) : (
              <Select
                value={editedSpeakerId || ''}
                onValueChange={(value) => {
                  handleSpeakerEdit(value);
                }}
              >
                <SelectTrigger
                  className={`h-6 w-auto justify-start gap-2 border-0 bg-transparent px-0 text-xs font-semibold shadow-none focus:ring-0 text-${color}-500`}
                >
                  {/* Use displayName as placeholder for internal-only speakers e.g. Guest 1 */}
                  <SelectValue placeholder={displayName} />
                </SelectTrigger>
                <SelectContent>
                  {speakers.map((speaker) => (
                    <SelectItem key={speaker.id} value={speaker.id}>
                      {speaker.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Content with inline diarization correction */}
      <div className="flex items-start gap-2 pl-[62px]">
        {!isEditing ? (
          <>
            <div className={`flex-1 text-sm leading-relaxed ${isPartial ? 'text-gray-500 italic' : 'text-gray-800'}`}>
              {block.content}

              {!isPartial && (
                <span>
                  <button
                    className="ml-1.5 inline rounded-sm bg-gray-100 p-0.5 pb-0.75 text-gray-500 duration-150 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                  >
                    <PenSquare size={12} className="-mb-0.25" />
                  </button>
                </span>
              )}
            </div>

            {/* Diarization correction - inline with content */}
            {block.correction && (
              <DiarizationCorrectionPopover
                correction={block.correction}
                speakers={speakers}
                isOpen={openPopoverId === block.correction.id}
                onOpenChange={handlePopoverChange}
                onAccept={() => onAcceptCorrection?.(block.correction!.id, block.id)}
                onReject={() => onRejectCorrection?.(block.correction!.id, block.id)}
                isProcessing={isProcessing}
              >
                <DiarizationCorrectionIndicator
                  correctionId={block.correction.id}
                  confidence={block.correction.confidence}
                />
              </DiarizationCorrectionPopover>
            )}
          </>
        ) : (
          <div className="flex items-center gap-1">
            <textarea
              ref={textareaRef}
              className="field-sizing-content grow resize-none rounded-md border border-gray-200 p-2 text-sm transition-colors duration-150 focus:border-blue-300"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  handleCancelEdit();
                  e.preventDefault();
                } else if (e.key === 'Enter' && !e.shiftKey) {
                  handleContentEdit(editedContent);
                  setIsEditing(false);
                  e.preventDefault();
                }
              }}
            />

            <button
              className="h-fit w-fit rounded-sm border border-gray-200 bg-gray-50 p-0.5 text-xs text-gray-600"
              onClick={() => handleContentEdit(editedContent)}
            >
              <Check size={14} />
            </button>
          </div>
        )}
      </div>

      {isPartial && <div className="mt-2 pl-[62px] text-xs text-yellow-600">(partial result)</div>}

      {children}
    </div>
  );
};
